import {
    type Social,
    SocialButton,
    type SocialButtonTheme,
} from '@trustoo/components/SocialButton/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Social button (v2) showcase ----------
// A brand-artwork component: the official third-party marks are inlined as SVG
// and the Facebook brand fill uses an exact brand hex that has no
// Tailwind token. Generated from Figma's "Social button" set by the
// component-sync pipeline.

const SOCIALS: Social[] = ['google', 'facebook', 'apple', 'x']
const THEMES: SocialButtonTheme[] = ['brand', 'color', 'gray']

const THEME_DESCRIPTION: Record<SocialButtonTheme, string> = {
    brand: "The platform's official treatment — Google's white field, the others filled with the brand colour.",
    color: 'White field, border, gray label, full-colour logo.',
    gray: 'White field, border, gray label, monochrome gray logo.',
}

const socialLabel = (social: Social): string =>
    social === 'x' ? 'X' : social.charAt(0).toUpperCase() + social.slice(1)

const SOCIAL_BUTTON_USAGE = `import { SocialButton } from '@trustoo/components/SocialButton/v2'

// Default: brand theme, labelled
<SocialButton social="google" onClick={signInWithGoogle} />

// Custom label
<SocialButton social="apple" label="Continue with Apple" />

// Theme: brand (official) · color (full-colour logo) · gray (monochrome)
<SocialButton social="facebook" theme="color" />

// Icon-only (44px square — aria-label is derived from the platform)
<SocialButton social="x" iconOnly />`

export const SocialButtonShowcase: React.FC = () => (
    <Section
        title="Social button (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Social button</span> set
                (ComponentSet <InlineCode>5:4825</InlineCode>). A{' '}
                <span className="font-semibold">brand-artwork</span> component: the official
                third-party marks are inlined as SVG and the Facebook brand fill uses an exact brand
                hex with no Tailwind token. Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/SocialButton/v2</InlineCode> (net-new component —
                there is no v1 barrel).
            </>
        }>
        <div className="space-y-spacing-3xl">
            {THEMES.map((theme) => (
                <div
                    key={theme}
                    className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <div className="mb-spacing-xs flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">
                            {theme.charAt(0).toUpperCase() + theme.slice(1)}
                        </span>
                        <InlineCode>theme=&quot;{theme}&quot;</InlineCode>
                    </div>
                    <p className="mb-spacing-lg text-xs text-text-tertiary">
                        {THEME_DESCRIPTION[theme]}
                    </p>
                    <div className="flex flex-wrap items-center gap-spacing-lg">
                        {SOCIALS.map((social) => (
                            <SocialButton key={social} social={social} theme={theme} />
                        ))}
                    </div>
                    <div className="mt-spacing-lg flex flex-wrap items-center gap-spacing-lg">
                        {SOCIALS.map((social) => (
                            <SocialButton
                                key={`${social}-icon`}
                                social={social}
                                theme={theme}
                                iconOnly
                            />
                        ))}
                    </div>
                </div>
            ))}

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={SOCIAL_BUTTON_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>social</InlineCode> (
                    {SOCIALS.map((s) => socialLabel(s)).join(' · ')}),{' '}
                    <InlineCode>theme</InlineCode> (brand · color · gray),{' '}
                    <InlineCode>iconOnly</InlineCode> (44px square, derives its{' '}
                    <InlineCode>aria-label</InlineCode>), and <InlineCode>label</InlineCode> to
                    override the default &quot;Sign in with …&quot; text. All native{' '}
                    <InlineCode>&lt;button&gt;</InlineCode> props pass through, and the component
                    forwards its ref. Hover or tab onto any button to see the native states.
                </p>
            </div>
        </div>
    </Section>
)
